import { useEffect, useState } from "react";
import { Save, Loader2, Trash2, Mail, MailOpen, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminPageHeader } from "@/components/admin/AdminBits";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function ContactInfoTab() {
  const [info, setInfo] = useState({ phone: "", email: "", address: "", extra: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/settings").then((r) => { setInfo({ phone: "", email: "", address: "", extra: "", ...r.data.contact }); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/contact-info", info); toast.success("Contact info updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const field = "w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20";
  if (loading) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div className="max-w-xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="space-y-4">
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Phone</label><input value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} data-testid="contact-info-phone" className={field} /></div>
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Email</label><input value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} data-testid="contact-info-email" className={field} /></div>
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Address <span className="text-slate-400">(optional)</span></label><textarea rows={2} value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} data-testid="contact-info-address" className={field} /></div>
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Additional info <span className="text-slate-400">(optional)</span></label><textarea rows={2} value={info.extra} onChange={(e) => setInfo({ ...info, extra: e.target.value })} data-testid="contact-info-extra" className={field} /></div>
        <button onClick={save} disabled={saving} data-testid="contact-info-save" className="inline-flex items-center gap-2 rounded-full bg-ukf-red px-5 py-2.5 text-sm font-semibold text-white hover:bg-ukf-red-dark disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
      </div>
    </div>
  );
}

function MessagesTab() {
  const [msgs, setMsgs] = useState([]);
  const load = () => api.get("/admin/messages").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const markRead = async (m) => { if (m.read) return; try { await api.put(`/admin/messages/${m.id}/read`); load(); } catch { /* */ } };
  const del = async (id) => { try { await api.delete(`/admin/messages/${id}`); toast.success("Message deleted"); load(); } catch { toast.error("Delete failed"); } };

  if (msgs.length === 0) return <p className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500">No messages received yet.</p>;

  return (
    <div className="space-y-3" data-testid="contact-messages-list">
      {msgs.map((m) => (
        <div key={m.id} data-testid={`message-${m.id}`} onClick={() => markRead(m)} className={`cursor-pointer rounded-xl border p-5 transition-colors ${m.read ? "border-slate-200 bg-white" : "border-ukf-gold/50 bg-ukf-gold/5"}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {m.read ? <MailOpen className="h-4 w-4 text-slate-400" /> : <Mail className="h-4 w-4 text-ukf-red" />}
                <p className="font-semibold text-ukf-ink">{m.subject || "(No subject)"}</p>
              </div>
              <p className="mt-1 text-sm text-slate-600">{m.name} · <a href={`mailto:${m.email}`} className="text-ukf-red hover:underline" onClick={(e) => e.stopPropagation()}>{m.email}</a></p>
              <p className="mt-3 whitespace-pre-line text-sm text-slate-700">{m.message}</p>
              <p className="mt-3 text-xs text-slate-400">{new Date(m.created_at).toLocaleString("en-GB")}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); del(m.id); }} data-testid={`message-delete-${m.id}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-ukf-red"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SecurityTab() {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState(""); const [busy, setBusy] = useState(false);
  const field = "w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20";
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) return toast.error("New password must be at least 8 characters");
    if (nw !== cf) return toast.error("Passwords do not match");
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password changed"); setCur(""); setNw(""); setCf(""); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Failed"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="change-password-form" className="max-w-md space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Current password</label><input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="current-password-input" className={field} /></div>
      <div><label className="mb-1.5 block text-sm font-medium text-slate-700">New password</label><input type="password" required value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password-input" className={field} /></div>
      <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Confirm new password</label><input type="password" required value={cf} onChange={(e) => setCf(e.target.value)} data-testid="confirm-password-input" className={field} /></div>
      <button type="submit" disabled={busy} data-testid="change-password-submit" className="inline-flex items-center gap-2 rounded-full bg-ukf-red px-5 py-2.5 text-sm font-semibold text-white hover:bg-ukf-red-dark disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Change password</button>
    </form>
  );
}

export default function AdminContact() {
  return (
    <div data-testid="admin-contact-page">
      <AdminPageHeader title="Contact" subtitle="Contact details, messages and account security." />
      <Tabs defaultValue="info">
        <TabsList className="mb-6">
          <TabsTrigger value="info" data-testid="tab-info">Contact info</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
        </TabsList>
        <TabsContent value="info"><ContactInfoTab /></TabsContent>
        <TabsContent value="messages"><MessagesTab /></TabsContent>
        <TabsContent value="security"><SecurityTab /></TabsContent>
      </Tabs>
    </div>
  );
}
